import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { dealerErrorMessage, dealersApi } from '../services/dealersApi';
import { Dealer, DEALER_STATUSES, DealerStatus } from '../types/dealers';
import CursorPagination from '../components/CursorPagination';
import DealerForm from '../components/dealers/DealerForm';
import DealerStatusChip from '../components/dealers/DealerStatusChip';
import FeatureOffBanner, { useFeatureOff } from '../components/dealers/FeatureOffBanner';
import { formatDate, humanize, pct } from '../components/dealers/dealerFormat';

const TABS: { key: DealerStatus | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All' },
  ...DEALER_STATUSES.map((s) => ({ key: s, label: humanize(s) })),
];

const PAGE_SIZE = 20;

/** Spec 085 Flow A — the dealer list (route /dealers, screen `dealers`). */
export default function DealersPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { off, catchOff, markOff, walletOff } = useFeatureOff(auth.token);
  const [tab, setTab] = useState<DealerStatus | 'ALL'>('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<Dealer[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const fetchPage = useCallback(async (after: string | null) => {
    if (!auth.token) return;
    if (after) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const page = await dealersApi.list({
        status: tab === 'ALL' ? undefined : tab, q: search || undefined, cursor: after ?? undefined, limit: PAGE_SIZE,
      }, auth.token);
      setRows((prev) => (after ? [...prev, ...page.content] : page.content));
      setCursor(page.nextCursor ?? null);
      setHasMore(page.hasMore);
    } catch (err) {
      if (!catchOff(err)) setError(dealerErrorMessage(err, 'Failed to load dealers'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [auth.token, tab, search, catchOff]);

  useEffect(() => { fetchPage(null); }, [fetchPage]);

  const onCreated = (saved: Dealer) => {
    setCreating(false);
    const warn = saved.breakEven?.warning ? ' · discount is at or above break-even' : '';
    navigate(`/dealers/${saved.id}`, { state: { toast: `${saved.tradeName} created as a draft${warn}` } });
  };

  const unfiltered = tab === 'ALL' && !search;
  const canSaveAboveBreakEven = auth.isSuperAdmin || auth.hasScreen('finance-config');

  return (
    <div>
      <div className="dealer-page-head">
        <h2 className="dealer-page-title">Dealers</h2>
        <div className="dealer-page-actions">
          {/* Packs are a wallet surface: gone once the wallet switch is known off (Maher 2026-11-03). */}
          {!walletOff && <Link to="/dealers/packs" className="admin-btn admin-btn-outline">Credit packs</Link>}
          <button type="button" className="admin-btn admin-btn-primary" onClick={() => setCreating(true)} disabled={off}>New dealer</button>
        </div>
      </div>

      <FeatureOffBanner off={off} />

      <div className="admin-tabs dealer-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            className={`promo-tab${tab === t.key ? ' active' : ''}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="admin-card">
        <div className="admin-toolbar">
          <input type="text" className="admin-search-input" placeholder="Search by name..." aria-label="Search dealers"
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>

        {loading ? (
          <div className="admin-loading" data-testid="dealers-loading"><div className="admin-loading-spinner" /></div>
        ) : error ? (
          <div data-testid="dealers-error">
            <div className="admin-info-banner danger" role="alert">{error}</div>
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => fetchPage(null)}>Retry</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="admin-empty-state" data-testid="dealers-empty">
            {unfiltered ? (
              <>
                <p>No dealers yet</p>
                {!off && <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setCreating(true)}>New dealer</button>}
              </>
            ) : <p>No dealers match these filters.</p>}
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table dealer-table">
              <thead>
                <tr>
                  <th>Trade name</th><th>Legal name</th><th>Status</th><th className="num">Discount</th>
                  <th>Agreement</th><th>Contact</th><th>Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 600 }}><Link to={`/dealers/${d.id}`} className="admin-link">{d.tradeName}</Link></td>
                    <td>{d.legalName}</td>
                    <td><DealerStatusChip status={d.status} /></td>
                    <td className="num">{pct(d.discountPct)}</td>
                    <td>
                      <span className={`admin-badge dealer-badge ${d.agreementOnFile ? 'approved' : 'pending'}`}>
                        {d.agreementOnFile ? 'On file' : 'Missing'}
                      </span>
                    </td>
                    <td>
                      <div>{d.contact.name}</div>
                      <div className="dealer-cell-note">{d.contact.phone}</div>
                    </td>
                    <td>{formatDate(d.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && <CursorPagination hasMore={hasMore} loading={loadingMore} onLoadMore={() => fetchPage(cursor)} />}
      </div>

      {creating && auth.token && (
        <DealerForm
          dealer={null}
          token={auth.token}
          canSaveAboveBreakEven={canSaveAboveBreakEven}
          onClose={() => setCreating(false)}
          onSaved={onCreated}
          onFeatureOff={markOff}
        />
      )}
    </div>
  );
}
